import React, { useRef, useState, useEffect } from 'react';
import { useOrderStore } from '../../store/orderStore';
import { Button } from '../ui/button';
import { X, ZoomIn, ZoomOut, Image as ImageIcon, Layout, Maximize, Minimize, Crop, RotateCw } from 'lucide-react';


import { ProductPreview, LayoutInfo } from './ProductPreview';
import { InstallationPreview } from './InstallationPreview';
import { PrintAreaEditor } from './PrintAreaEditor';

export const CenterPreview: React.FC = () => {
    const {
        uploadedImage,
        setImage,
        customPaperWidth,
        customPaperHeight,
        productOptions,
        selectedFrameProduct,
        setProductOption
    } = useOrderStore();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const previewContainerRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);
    const [previewMode, setPreviewMode] = useState<'product' | 'installation'>('product');
    const [isFullScreen, setIsFullScreen] = useState(false);

    // Editor State
    const [isEditing, setIsEditing] = useState(false);
    const [layoutInfo, setLayoutInfo] = useState<LayoutInfo | null>(null);
    const [imgSize, setImgSize] = useState<{ w: number, h: number } | null>(null);

    useEffect(() => {
        if (!uploadedImage) {
            setImgSize(null);
            return;
        }
        const img = new Image();
        img.onload = () => setImgSize({ w: img.width, h: img.height });
        img.src = uploadedImage;
    }, [uploadedImage]);

    // オプション値の取得
    const borderMode = productOptions['radio_1526522940']; // フチの処理方法
    const borderTop = Number(productOptions['number_7367551325']) || 0;
    const borderBottom = Number(productOptions['number-7625311423']) || 0;
    const borderLeft = Number(productOptions['number-6908047827']) || 0;
    const borderRight = Number(productOptions['number-6909567831']) || 0;

    const specifiedImageWidth = Number(productOptions['number_0047761462']);
    const specifiedImageHeight = Number(productOptions['number-1474802223']);

    // 用紙サイズ (mm)
    const paperWidth = customPaperWidth || 210;
    const paperHeight = customPaperHeight || 297;

    // フチ幅の計算ロジック
    let finalBorderTop = borderTop;
    let finalBorderBottom = borderBottom;
    let finalBorderLeft = borderLeft;
    let finalBorderRight = borderRight;

    // マット加工のサイズ計算
    let matTop = 0;
    let matBottom = 0;
    let matLeft = 0;
    let matRight = 0;

    const matMethod = productOptions['mat_window_size_method'];
    if (selectedFrameProduct && (selectedFrameProduct.title.includes('ブックマット') || selectedFrameProduct.title.includes('オーバーマット'))) {
        let windowW = 0;
        let windowH = 0;

        if (matMethod === '定形サイズ') {
            const matStdVal = productOptions['mat_window_standard_size'];
            const MAT_STD_SIZES: Record<string, { w: number, h: number }> = {
                "A5": { w: 148, h: 210 },
                "A4": { w: 210, h: 297 },
                "A3": { w: 297, h: 420 },
                "A3+": { w: 329, h: 483 },
                "A2": { w: 420, h: 594 },
                "B5": { w: 182, h: 257 },
                "B4": { w: 257, h: 364 },
                "B3": { w: 364, h: 515 },
                "2L": { w: 127, h: 178 },
                "mutsugiri": { w: 203, h: 254 },
                "wide_yotsugiri": { w: 254, h: 365 },
                "hansetsu": { w: 356, h: 432 },
                "zenshi": { w: 457, h: 560 },
            };
            if (matStdVal && MAT_STD_SIZES[matStdVal]) {
                windowW = MAT_STD_SIZES[matStdVal].w;
                windowH = MAT_STD_SIZES[matStdVal].h;
            }
        } else if (matMethod === '定形外サイズ') {
            const w = Number(productOptions['mat_window_short_side']);
            const h = Number(productOptions['mat_window_long_side']);
            if (!isNaN(w) && !isNaN(h) && w > 0 && h > 0) {
                windowW = w;
                windowH = h;
            }
        }

        // マット内窓サイズが有効な場合、マット幅を計算
        if (windowW > 0 && windowH > 0) {
            // paperWidth/Height = Frame Inner Size
            const verticalMargin = Math.max(0, (paperHeight - windowH) / 2);
            const horizontalMargin = Math.max(0, (paperWidth - windowW) / 2);

            matTop = verticalMargin;
            matBottom = verticalMargin;
            matLeft = horizontalMargin;
            matRight = horizontalMargin;
        }
    } else if (borderMode === '画像サイズを指定する' && specifiedImageWidth > 0 && specifiedImageHeight > 0) {
        // 画像サイズ指定モード (Standard Border Logic)
        const verticalMargin = Math.max(0, (paperHeight - specifiedImageHeight) / 2);
        const horizontalMargin = Math.max(0, (paperWidth - specifiedImageWidth) / 2);
        finalBorderTop = verticalMargin;
        finalBorderBottom = verticalMargin;
        finalBorderLeft = horizontalMargin;
        finalBorderRight = horizontalMargin;
    }

    // 額装シミュレーションを表示するかどうか判定
    const isFrameSelected = selectedFrameProduct && (
        selectedFrameProduct.title.includes('アルミ製フレーム') ||
        selectedFrameProduct.title.includes('木製フレーム')
    );

    const handleUpdateBorders = (top: number, bottom: number, left: number, right: number) => {
        setProductOption('number_7367551325', top);
        setProductOption('number-7625311423', bottom);
        setProductOption('number-6908047827', left);
        setProductOption('number-6909567831', right);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const reader = new FileReader();
            reader.onload = (event) => {
                setImage(event.target?.result as string);
            };
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const reader = new FileReader();
            reader.onload = (event) => {
                setImage(event.target?.result as string);
            };
            reader.readAsDataURL(e.dataTransfer.files[0]);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
    };

    const handleRemoveImage = () => {
        setImage(null);
        setScale(1);
    };

    const handleRotateImage = () => {
        if (!uploadedImage) return;

        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = uploadedImage;
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.height;
            canvas.height = img.width;

            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            ctx.translate(canvas.width / 2, canvas.height / 2);
            ctx.rotate((90 * Math.PI) / 180);
            ctx.drawImage(img, -img.width / 2, -img.height / 2);

            setImage(canvas.toDataURL());
        };
    };

    const toggleFullScreen = () => {
        if (!document.fullscreenElement) {
            previewContainerRef.current?.requestFullscreen().catch(err => {
                console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
            });
        } else {
            document.exitFullscreen();
        }
    };

    useEffect(() => {
        const handleFullScreenChange = () => {
            setIsFullScreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullScreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullScreenChange);
        };
    }, []);

    return (
        <div
            ref={previewContainerRef}
            className="flex-1 bg-gray-200 w-full h-full flex flex-col items-center justify-start p-4 lg:p-8 relative overflow-hidden"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
        >
            {/* Top Controls: Moved outside to avoid overlap */}
            {uploadedImage && (
                <div className="w-full flex justify-between items-start mb-2 px-1 relative h-0 z-50">
                    {/* Resolution Info */}
                    {previewMode === 'product' && layoutInfo && imgSize && (() => {
                        const { scale, PIXELS_PER_MM, contentWidthMm, contentHeightMm, frameWidthMm, matLeft, matRight, matTop, matBottom } = layoutInfo;
                        const { w: imgW, h: imgH } = imgSize;

                        const contentW = contentWidthMm * PIXELS_PER_MM * scale;
                        // const contentH = contentHeightMm * PIXELS_PER_MM * scale;

                        const regionW = contentW - ((matLeft + finalBorderLeft + matRight + finalBorderRight) * PIXELS_PER_MM * scale);
                        // const regionH = (contentHeightMm * PIXELS_PER_MM * scale) - ((matTop + finalBorderTop + matBottom + finalBorderBottom) * PIXELS_PER_MM * scale);

                        const imgAspect = imgW / imgH;
                        // const regionAspect = regionW / regionH; // unused

                        let drawW, drawH;
                        // Simplified logic just for ppi calculation. We don't need precise drawW/H for PPI if we trust regionW is the print width?
                        // Actually, drawW is the printed image width on canvas.
                        // Re-using logic:
                        const regionH = (contentHeightMm * PIXELS_PER_MM * scale) - ((matTop + finalBorderTop + matBottom + finalBorderBottom) * PIXELS_PER_MM * scale);
                        const regionAspect = regionW / regionH;

                        if (imgAspect > regionAspect) {
                            drawW = regionW;
                            drawH = regionW / imgAspect;
                        } else {
                            drawH = regionH;
                            drawW = regionH * imgAspect;
                        }

                        const printedWidthMm = drawW / (PIXELS_PER_MM * scale);
                        const printedHeightMm = drawH / (PIXELS_PER_MM * scale);

                        const ppiW = imgW / (printedWidthMm / 25.4);
                        const ppi = Math.round(ppiW);
                        const isLowRes = ppi < 150;

                        return (
                            <div className="absolute top-0 left-0 bg-white/90 p-1.5 shadow-sm text-[10px] leading-tight z-50 pointer-events-none border border-gray-200 rounded -translate-y-4 -translate-x-4">
                                <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 items-center">
                                    <span className="text-gray-500">画像:</span>
                                    <span className="text-right font-mono">{imgW}×{imgH}px</span>

                                    <span className="text-gray-500">印刷:</span>
                                    <span className="text-right font-mono">{Math.round(printedWidthMm)}×{Math.round(printedHeightMm)}mm</span>

                                    <span className="text-gray-500">解像度:</span>
                                    <span className={`text-right font-bold ${isLowRes ? 'text-red-600' : 'text-blue-600'}`}>
                                        {ppi} ppi
                                    </span>
                                </div>
                                {isLowRes && (
                                    <div className="mt-1 text-red-600 font-bold bg-red-50 p-1 rounded border border-red-200 text-center">
                                        ⚠️ 入稿不可 (150ppi未満)
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* Debug Button */}
                    <div className="absolute top-0 right-0 -translate-y-4 translate-x-4 z-50">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setImage('/sample.jpg')}
                        >
                            Debug: Load Image
                        </Button>
                    </div>
                </div>
            )}
            {/* ① プレビュー領域 (Preview Area) */}
            <div className="relative w-full h-full lg:h-[700px] flex items-center justify-center border border-gray-400 bg-gray-100 overflow-hidden">

                {uploadedImage ? (
                    previewMode === 'product' ? (
                        <>
                            <ProductPreview
                                widthMm={paperWidth}
                                heightMm={paperHeight}
                                imageUrl={uploadedImage}
                                frameType={isFrameSelected ? 'aluminum_black' : undefined}
                                borderTop={finalBorderTop}
                                borderBottom={finalBorderBottom}
                                borderLeft={finalBorderLeft}
                                borderRight={finalBorderRight}
                                matTop={matTop}
                                matBottom={matBottom}
                                matLeft={matLeft}
                                matRight={matRight}
                                productTitle={selectedFrameProduct?.title}
                                onLayoutChange={setLayoutInfo}
                            />
                            {isEditing && layoutInfo && (
                                <PrintAreaEditor
                                    layout={layoutInfo}
                                    borderTop={finalBorderTop}
                                    borderBottom={finalBorderBottom}
                                    borderLeft={finalBorderLeft}
                                    borderRight={finalBorderRight}
                                    onUpdateBorders={handleUpdateBorders}
                                />
                            )}
                        </>
                    ) : (
                        <InstallationPreview
                            widthMm={paperWidth}
                            heightMm={paperHeight}
                            imageUrl={uploadedImage}
                            frameType={isFrameSelected ? 'aluminum_black' : undefined}
                        />
                    )
                ) : (
                    <div className="text-center">
                        <div
                            className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-12 cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-xl font-bold text-gray-800 mb-2">画像をアップロード</h3>
                            <p className="text-gray-500 mb-6">
                                ここに画像をドラッグ＆ドロップ<br />
                                またはクリックして選択
                            </p>
                            <Button>画像を選択する</Button>
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleFileChange}
                        />
                        <Button
                            variant="outline"
                            className="mt-4"
                            onClick={() => setImage('/sample.jpg')}
                        >
                            Debug: Load Test Image
                        </Button>
                    </div>
                )}
            </div>
            {/* Bottom Controls: Moved outside */}
            {uploadedImage && (
                <div className="mt-4 flex flex-col items-center space-y-2 z-10 w-full px-4">
                    <div className="flex items-center justify-center space-x-4">
                        {/* Mode Switch */}
                        <div className="bg-white p-1 rounded-lg shadow-md flex">
                            <button
                                onClick={() => {
                                    setPreviewMode('product');
                                    setIsEditing(false);
                                }}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2 ${previewMode === 'product' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                            >
                                <ImageIcon className="w-4 h-4" />
                                <span>商品のみ</span>
                            </button>
                            <button
                                onClick={() => {
                                    setPreviewMode('installation');
                                    setIsEditing(false);
                                }}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2 ${previewMode === 'installation' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                            >
                                <Layout className="w-4 h-4" />
                                <span>設置イメージ</span>
                            </button>
                        </div>

                        {/* Edit Button */}
                        {previewMode === 'product' && (
                            <div className="bg-white p-1 rounded-lg shadow-md">
                                <button
                                    onClick={() => setIsEditing(!isEditing)}
                                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2 ${isEditing ? 'bg-green-100 text-green-700' : 'text-gray-600 hover:bg-gray-100'}`}
                                >
                                    <Crop className="w-4 h-4" />
                                    <span>{isEditing ? '編集終了' : '画像編集'}</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Utility Buttons: Rotate, Fullscreen, Delete */}
                    <div className="flex space-x-4 bg-white p-2 rounded-full shadow-lg">
                        <Button variant="ghost" size="icon" onClick={handleRotateImage} className="text-gray-600 hover:text-gray-800 hover:bg-gray-100" title="画像を回転">
                            <RotateCw className="w-5 h-5" />
                        </Button>
                        <div className="w-px bg-gray-200 mx-2" />
                        <Button variant="ghost" size="icon" onClick={toggleFullScreen} className="text-gray-600 hover:text-gray-800 hover:bg-gray-100">
                            {isFullScreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
                        </Button>
                        <div className="w-px bg-gray-200 mx-2" />
                        <Button variant="ghost" size="icon" onClick={handleRemoveImage} className="text-red-500 hover:text-red-600 hover:bg-red-50">
                            <X className="w-5 h-5" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};
